import { useState, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { getLastDayOfMonth } from '../lib/utils';

export const getPreviousMonthValue = (monthStr) => {
  const [year, month] = monthStr.split('-').map(Number);
  const date = new Date(year, month - 1);
  date.setMonth(date.getMonth() - 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

async function ensureAllocsForMonth(cats, month, userId, signal) {
  if (!cats.length) return new Map();
  const prevMonth = getPreviousMonthValue(month);
  const [{ data: current }, { data: prev }] = await Promise.all([
    supabase.from('allocations').select('category_id, amount').eq('user_id', userId).eq('month', month),
    supabase.from('allocations').select('category_id, amount').eq('user_id', userId).eq('month', prevMonth),
  ]);
  if (signal?.aborted) return null;
  const currentMap = new Map((current || []).map(a => [a.category_id, a.amount]));
  const prevMap = new Map((prev || []).map(a => [a.category_id, a.amount]));
  const inserts = [];
  cats.forEach(cat => {
    if (!currentMap.has(cat.id)) {
      const amount = prevMap.get(cat.id) ?? 0;
      inserts.push({ user_id: userId, category_id: cat.id, month, amount });
      currentMap.set(cat.id, amount);
    }
  });
  if (inserts.length && !signal?.aborted) {
    await supabase.from('allocations').insert(inserts);
  }
  return currentMap;
}

export const useBudgetData = (user, currentMonth, setToast) => {
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [monthlyIncome, setMonthlyIncome] = useState(0);
  const [allocations, setAllocations] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [pendingRecurring, setPendingRecurring] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const toggleGroup = useCallback((groupId) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      return next;
    });
  }, []);

  const totalBudgeted = useMemo(
    () => categories.reduce((sum, cat) => sum + (allocations.get(cat.id) || 0), 0),
    [categories, allocations]
  );
  const totalSpent = useMemo(
    () => transactions.filter(t => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount || 0), 0),
    [transactions]
  );
  const transactionsByCategory = useMemo(() =>
    transactions.reduce((acc, txn) => {
      const key = txn.category_id || 'uncategorized';
      (acc[key] = acc[key] || []).push(txn);
      return acc;
    }, {}),
    [transactions]
  );

  const refreshData = useCallback(async (signal) => {
    if (!user?.id) { setLoading(false); return; }
    try {
      const prevMonth = getPreviousMonthValue(currentMonth);
      const [
        { data: groups, error: groupsError },
        { data: cats, error: catsError },
        { data: txns, error: txnsError },
        { data: prevRecurring },
      ] = await Promise.all([
        supabase.from('category_groups').select('*').eq('user_id', user.id).order('sort_order', { ascending: true }),
        supabase.from('categories').select('*').eq('user_id', user.id).order('sort_order', { ascending: true }),
        supabase.from('transactions').select('*').eq('user_id', user.id)
          .gte('date', `${currentMonth}-01`).lte('date', getLastDayOfMonth(currentMonth))
          .order('date', { ascending: false }),
        supabase.from('transactions').select('*').eq('user_id', user.id)
          .eq('is_recurring', true)
          .gte('date', `${prevMonth}-01`).lte('date', getLastDayOfMonth(prevMonth)),
      ]);

      if (signal?.aborted) return;
      if (groupsError || catsError || txnsError) setToast({ message: 'Load error', type: 'error' });

      const allocMap = await ensureAllocsForMonth(cats || [], currentMonth, user.id, signal);
      if (signal?.aborted) return;

      const { data: incomeData } = await supabase.from('monthly_income')
        .select('amount').eq('user_id', user.id).eq('month', currentMonth).single();
      if (signal?.aborted) return;

      setCategoryGroups(groups || []);
      setCategories(cats || []);
      setTransactions(txns || []);
      setAllocations(allocMap || new Map());
      setMonthlyIncome(incomeData?.amount || 0);
      setExpandedGroups(new Set((groups || []).map(g => g.id)));
      const today = new Date();
      const todayMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
      const todayDay = today.getDate();
      setPendingRecurring(
        (prevRecurring || []).filter(prev => {
          if ((txns || []).some(curr =>
            curr.category_id === prev.category_id && curr.recurrence_day === prev.recurrence_day
          )) return false;
          if (currentMonth === todayMonth) return prev.recurrence_day <= todayDay;
          return currentMonth < todayMonth;
        })
      );
      setLoading(false);
    } catch (err) {
      if (signal?.aborted) return;
      console.error('refreshData:', err);
      setToast({ message: 'Load error', type: 'error' });
      setLoading(false);
    }
  }, [user, currentMonth, setToast]);

  return {
    categoryGroups, setCategoryGroups,
    categories, setCategories,
    transactions, setTransactions,
    monthlyIncome, setMonthlyIncome,
    allocations, setAllocations,
    loading,
    pendingRecurring, setPendingRecurring,
    expandedGroups, setExpandedGroups, toggleGroup,
    totalBudgeted, totalSpent,
    available: monthlyIncome - totalSpent,
    transactionsByCategory,
    refreshData,
  };
};
